import { Folder, PictureAsPdf, Description, Image, VideoFile, AudioFile, Archive, Code, GetApp } from '@mui/icons-material'
import { AspectRatio, Box, Card, CardOverflow, Typography, IconButton, Tooltip, ColorPaletteProp } from '@mui/joy'
import { FetchedEmailAttachment } from '../../../../../../../../../../../../../types/fetched-email/types'
import axios from 'axios'

interface EmailAttachmentProps {
    attachment: FetchedEmailAttachment
    messageId: string
    grantId: string
}

const getFileIcon = (filename: string, mimeType?: string) => {
    const extension = filename?.split('.').pop()?.toLowerCase()

    // Check by mime type first
    if (mimeType) {
        if (mimeType.startsWith('image/')) return <Image sx={{ fontSize: '1.5rem', color: 'success.main' }} />
        if (mimeType.startsWith('video/')) return <VideoFile sx={{ fontSize: '1.5rem', color: 'info.main' }} />
        if (mimeType.startsWith('audio/')) return <AudioFile sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
        if (mimeType === 'application/pdf') return <PictureAsPdf sx={{ fontSize: '1.5rem', color: 'error.main' }} />
    }

    // Check by extension
    switch (extension) {
        case 'pdf':
            return <PictureAsPdf sx={{ fontSize: '1.5rem', color: 'error.main' }} />
        case 'doc':
        case 'docx':
        case 'txt':
        case 'rtf':
            return <Description sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'bmp':
        case 'svg':
            return <Image sx={{ fontSize: '1.5rem', color: 'success.main' }} />
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'wmv':
            return <VideoFile sx={{ fontSize: '1.5rem', color: 'info.main' }} />
        case 'mp3':
        case 'wav':
        case 'flac':
            return <AudioFile sx={{ fontSize: '1.5rem', color: 'warning.main' }} />
        case 'zip':
        case 'rar':
        case '7z':
            return <Archive sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
        case 'js':
        case 'ts':
        case 'html':
        case 'css':
        case 'json':
            return <Code sx={{ fontSize: '1.5rem', color: 'primary.main' }} />
        default:
            return <Folder sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
    }
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const getFileTypeColor = (filename: string, mimeType?: string): ColorPaletteProp => {
    const extension = filename?.split('.').pop()?.toLowerCase()

    if (mimeType) {
        if (mimeType.startsWith('image/')) return 'success' as ColorPaletteProp
        if (mimeType.startsWith('video/')) return 'primary' as ColorPaletteProp // Changed from 'info' to 'primary'
        if (mimeType.startsWith('audio/')) return 'warning' as ColorPaletteProp
        if (mimeType === 'application/pdf') return 'danger' as ColorPaletteProp
    }

    switch (extension) {
        case 'pdf':
            return 'danger' as ColorPaletteProp
        case 'doc':
        case 'docx':
        case 'txt':
            return 'primary' as ColorPaletteProp
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return 'success' as ColorPaletteProp
        case 'mp4':
        case 'avi':
        case 'mov':
            return 'primary' as ColorPaletteProp
        case 'mp3':
        case 'wav':
            return 'warning' as ColorPaletteProp
        default:
            return 'primary' as ColorPaletteProp
    }
}

export default function EmailAttachment({ attachment, messageId, grantId }: EmailAttachmentProps) {
    const handleDownload = async () => {
        try {
            const attachmentUrl = `/email/attachment?attachmentId=${attachment.id}&messageId=${messageId}&grantId=${grantId}`
            const response = await axios.get(attachmentUrl, {
                responseType: 'blob',
            })

            const url = window.URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = url
            link.download = attachment.filename || 'attachment'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Download failed:', error)
        }
    }

    return (
        <Card
            variant="outlined"
            orientation="horizontal"
            sx={{
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: 'md',
                    transform: 'translateY(-2px)',
                    borderColor: 'primary.outlinedBorder',
                },
            }}
            onClick={handleDownload}
        >
            <CardOverflow>
                <AspectRatio
                    ratio="1"
                    sx={{
                        minWidth: 80,
                        bgcolor: `${getFileTypeColor(attachment.filename, attachment.content_type)}.softBg`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.1)',
                            },
                        }}
                    >
                        {getFileIcon(attachment.filename, attachment.content_type)}
                    </Box>
                </AspectRatio>
            </CardOverflow>

            <Box
                sx={{
                    py: 1.5,
                    pr: 1,
                    pl: 1.5,
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography
                        level="title-sm"
                        sx={{
                            color: 'text.primary',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            mr: 1,
                        }}
                    >
                        {attachment.filename || 'Untitled attachment'}
                    </Typography>

                    <Tooltip title="Download file" placement="top">
                        <IconButton
                            size="sm"
                            variant="soft"
                            color={getFileTypeColor(attachment.filename, attachment.content_type)}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleDownload()
                            }}
                            sx={{
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'scale(1.1)',
                                },
                            }}
                        >
                            <GetApp sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {attachment.size && (
                        <Typography
                            level="body-xs"
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                            }}
                        >
                            {formatFileSize(attachment.size)}
                        </Typography>
                    )}

                    {attachment.content_type && (
                        <Typography
                            level="body-xs"
                            sx={{
                                color: 'text.tertiary',
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {attachment.content_type?.split('/')[1] || 'file'}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    )
}
